import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppConfig } from '@config/AppConfigContext';
import { fetchJson, getAuthHeader, joinUrl } from '../utils/_partner_api_client';

// ── Types ──────────────────────────────────────────────────────────────────────

export type CompanyUserSortField = 'createdAt' | 'totalMessages' | 'lastActive';
export type SortDirection = 'asc' | 'desc';

export interface CompanyUserRow {
  userId: string;
  firstName: string;
  companyId: string;
  companyName: string;
  organizationId: string;
  /** Human messages within the requested from/to range. */
  totalMessages: number;
  /** Most recent human message at any time — never restricted by from/to. */
  lastActive: string | null;
  createdAt: string | null;
}

export interface CompanyUsersPage {
  users: CompanyUserRow[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  /** All-time headcount for the company — unaffected by from/to. */
  totalUsers: number;
  from: string;
  to: string;
}

export interface UseCompanyUsersParams {
  companyId: string | null | undefined;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  sort?: CompanyUserSortField;
  dir?: SortDirection;
  /**
   * AceUpAdmin/AceUpViewer only — scopes the request to this organization.
   * Ignored (and unnecessary) for PartnerAdmin/PartnerViewer, whose org_id
   * always comes from the JWT.
   */
  organizationId?: string | null;
  enabled?: boolean;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Fetches a paginated, sortable page of a single company's users, each with
 * a total-messages-in-range + last-active-any-time stat attached.
 *
 * Backed by GET /api/v1/partner/companies/<companyId>/users (sc-43503,
 * company drill-down page).
 */
export const useCompanyUsersQuery = ({
  companyId,
  from,
  to,
  page,
  limit,
  sort,
  dir,
  organizationId,
  enabled = true,
}: UseCompanyUsersParams): UseQueryResult<CompanyUsersPage, Error> => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { allyApi, auth0Audience } = useAppConfig();

  return useQuery({
    queryKey: [
      'partner', 'companies', companyId, 'users', from, to, page, limit, sort, dir, organizationId,
    ] as const,
    enabled: Boolean(isAuthenticated && allyApi && enabled && companyId),
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<CompanyUsersPage> => {
      if (!allyApi) throw new Error('Missing allyApi configuration');
      if (!companyId) throw new Error('Missing companyId');

      const authHeader = await getAuthHeader(getAccessTokenSilently, auth0Audience);

      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (page != null) qs.set('page', String(page));
      if (limit != null) qs.set('limit', String(limit));
      if (sort) qs.set('sort', sort);
      if (dir) qs.set('dir', dir);
      if (organizationId) qs.set('organization_id', organizationId);

      return fetchJson<CompanyUsersPage>(
        joinUrl(allyApi, `api/v1/partner/companies/${encodeURIComponent(companyId)}/users?${qs}`),
        authHeader,
        'Failed to fetch company users'
      );
    },
  });
};
